import type {
  CindroHealthResponse as SharedCindroHealthResponse,
  CindroReleaseManifest as SharedCindroReleaseManifest,
  CindroRuntimeConfigRequest as SharedCindroRuntimeConfigRequest,
  CindroRuntimeConfigResponse as SharedCindroRuntimeConfigResponse,
  CindroUpdateStateRequest as SharedCindroUpdateStateRequest,
  CindroUpdateStateResponse as SharedCindroUpdateStateResponse,
  OtaPlatform as SharedCindroOtaPlatform,
  OtaRecommendedAction as SharedCindroOtaRecommendedAction
} from '@cindro/contracts';

export type CindroClientHeaders = Record<string, string>;

export type CindroOtaPlatform = SharedCindroOtaPlatform;
export type CindroOtaRecommendedAction = SharedCindroOtaRecommendedAction;
export type CindroHealthResponse = SharedCindroHealthResponse;
export type CindroReleaseManifest = SharedCindroReleaseManifest;
export type CindroUpdateStateResponse = SharedCindroUpdateStateResponse;
export type CindroRuntimeConfigRequest = SharedCindroRuntimeConfigRequest;
export type CindroRuntimeConfigResponse = SharedCindroRuntimeConfigResponse;

export type GetRuntimeConfigOptions = {
  // Overrides the derived URL entirely — for the same-origin nginx-proxied
  // web deploy path (e.g. /.well-known/cindro-runtime-config), which isn't
  // under this client's normal /api prefix. Native/default callers don't
  // need this.
  requestUrl?: string;
};

export type CindroClientConfig = {
  baseUrl?: string;
  headers?: CindroClientHeaders;
  fetchImpl?: typeof fetch;
};

export type CindroUpdateStateRequest = SharedCindroUpdateStateRequest;

export type CindroClient = {
  baseUrl: string;
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
  getHealth: () => Promise<CindroHealthResponse>;
  getWebUpdateState: (
    input?: CindroUpdateStateRequest
  ) => Promise<CindroUpdateStateResponse>;
  getRuntimeConfig: (
    input?: CindroRuntimeConfigRequest,
    options?: GetRuntimeConfigOptions
  ) => Promise<CindroRuntimeConfigResponse>;
};

export class CindroClientError extends Error {
  constructor(
    public readonly status: number,
    statusText: string,
    public readonly code?: string
  ) {
    super(
      `Cindro request failed (${status}${statusText ? ` ${statusText}` : ''})`
    );
    this.name = 'CindroClientError';
  }
}

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, '');
}

export function resolveCindroBaseUrl(explicitBaseUrl?: string) {
  const fromExplicit = explicitBaseUrl?.trim();
  if (fromExplicit) return trimTrailingSlash(fromExplicit);

  if (typeof window !== 'undefined') {
    const runtime = (window as any)?.cindroBaseUrl?.trim?.();
    if (runtime) return trimTrailingSlash(runtime);

    const controlPlane = (window as any)?.cindroControlPlaneBaseUrl?.trim?.();
    if (controlPlane) return trimTrailingSlash(controlPlane);

    return trimTrailingSlash(window.location.origin);
  }

  const envBaseUrl =
    process.env['NX_PUBLIC_CINDRO_SERVER_URL']?.trim() ||
    process.env['CINDRO_SERVER_URL']?.trim();

  return trimTrailingSlash(envBaseUrl || 'https://127.0.0.1:18443');
}

function joinUrl(baseUrl: string, path: string) {
  return new URL(path, `${trimTrailingSlash(baseUrl)}/`).toString();
}

function mergeHeaders(
  baseHeaders?: CindroClientHeaders,
  overrideHeaders?: HeadersInit
): CindroClientHeaders {
  const merged: CindroClientHeaders = { ...(baseHeaders || {}) };

  if (overrideHeaders instanceof Headers) {
    overrideHeaders.forEach((value, key) => {
      merged[key] = value;
    });
    return merged;
  }

  if (Array.isArray(overrideHeaders)) {
    for (const [key, value] of overrideHeaders) {
      merged[key] = value;
    }
    return merged;
  }

  return {
    ...merged,
    ...(overrideHeaders || {})
  };
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let code: string | undefined;
    try {
      const body = (await response.clone().json()) as { code?: unknown };
      if (
        typeof body.code === 'string' &&
        /^[A-Z][A-Z0-9_]{0,63}$/.test(body.code)
      )
        code = body.code;
    } catch {
      // Preserve the status-only behavior for empty or non-JSON error bodies.
    }
    throw new CindroClientError(response.status, response.statusText, code);
  }

  if (
    response.status === 204 ||
    response.headers.get('content-length') === '0'
  ) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function createCindroClient(
  config: CindroClientConfig = {}
): CindroClient {
  const fetchImpl = config.fetchImpl || globalThis.fetch?.bind(globalThis);

  if (!fetchImpl) {
    throw new Error(
      'A fetch implementation is required to create a Cindro client.'
    );
  }

  const baseUrl = resolveCindroBaseUrl(config.baseUrl);

  const request = async <T>(path: string, init?: RequestInit) => {
    const response = await fetchImpl(joinUrl(baseUrl, `/api${path}`), {
      ...init,
      headers: mergeHeaders(config.headers, init?.headers)
    });

    return parseJsonResponse<T>(response);
  };

  // Bypasses the /api prefix + resolved baseUrl entirely, for callers that
  // already built their own absolute URL (see GetRuntimeConfigOptions).
  const requestAbsolute = async <T>(url: string, init?: RequestInit) => {
    const response = await fetchImpl(url, {
      ...init,
      headers: mergeHeaders(config.headers, init?.headers)
    });

    return parseJsonResponse<T>(response);
  };

  return {
    baseUrl,
    request,
    getHealth: () => request<CindroHealthResponse>('/health'),
    getRuntimeConfig: (
      input: CindroRuntimeConfigRequest = {},
      options: GetRuntimeConfigOptions = {}
    ) => {
      const search = new URLSearchParams();

      if (input.organizationId)
        search.set('organizationId', input.organizationId);
      if (input.projectId) search.set('projectId', input.projectId);
      if (input.releaseAppVersionId) {
        search.set('releaseAppVersionId', input.releaseAppVersionId);
      }
      if (input.tenantEnvironmentId) {
        search.set('tenantEnvironmentId', input.tenantEnvironmentId);
      }
      if (input.platform) search.set('platform', input.platform);
      if (input.nativeAppId) search.set('nativeAppId', input.nativeAppId);
      if (input.nativeVersion) search.set('nativeVersion', input.nativeVersion);
      if (input.hostname) search.set('hostname', input.hostname);

      const suffix = search.toString() ? `?${search.toString()}` : '';

      if (options.requestUrl) {
        return requestAbsolute<CindroRuntimeConfigResponse>(
          `${options.requestUrl}${suffix}`
        );
      }

      return request<CindroRuntimeConfigResponse>(`/runtime/config${suffix}`);
    },
    getWebUpdateState: (input: CindroUpdateStateRequest = {}) => {
      const search = new URLSearchParams();

      if (input.currentVersion) {
        search.set('currentVersion', input.currentVersion);
      }

      if (input.channel) {
        search.set('channel', input.channel);
      }

      if (input.platform) {
        search.set('platform', input.platform);
      }

      if (input.organizationId) {
        search.set('organizationId', input.organizationId);
      }

      if (input.projectId) {
        search.set('projectId', input.projectId);
      }

      if (input.deviceId) {
        search.set('deviceId', input.deviceId);
      }

      const suffix = search.toString() ? `?${search.toString()}` : '';

      return request<CindroUpdateStateResponse>(`/web/update-state${suffix}`);
    }
  };
}
